import { NgModule } from "@angular/core";
import { PreloadAllModules, RouterModule, Routes } from "@angular/router";
import { NgxPermissionsGuard } from "ngx-permissions";
import { AuthCodeMobileComponent } from "./auth-code-mobile.component";
import { AuthCodeComponent } from "./auth-code.component";
import { RedirectToAppComponent } from "./redirect-to-app.component";
import {
  AuthGuard,
  IsMultiTenant,
  IsMultiTenantNot,
  NoAuthGuard,
} from "./services/user/auth.guard";
import {
  administrationPagePermissions,
  customerOrderPagePermissions,
  dashboardPagePermissions,
  managePagePermissions,
  operationsPagePermissions,
  reportsPageRoutes,
} from "./shared/side-menu/side-menu.component";
import {
  customerDimensionsPermissions,
  productsDimensionsPermissions,
} from "./utils/dimensionstypes";
import { APP_ROUTES } from "./utils/routes";
import { LogOutAppComponent } from "./log-out-app.component";

const routes: Routes = [
  {
    path: APP_ROUTES.ROOT,
    redirectTo: APP_ROUTES.AUTH,
    pathMatch: "full",
  },
  {
    path: "privacy-policy",
    loadChildren: () =>
      import("./modules/privacy-policy/privacy-policy.module").then(
        (m) => m.PrivacyPolicyModule,
      ),
  },
  {
    path: APP_ROUTES.CALLBACK,
    canActivate: [NoAuthGuard, IsMultiTenantNot],
    component: AuthCodeComponent,
  },
  {
    path: APP_ROUTES.CALLBACK_MOBILE,
    canActivate: [NoAuthGuard, IsMultiTenantNot],
    component: AuthCodeMobileComponent,
  },
  {
    path: APP_ROUTES.REDIRECT_TO_APP,
    component: RedirectToAppComponent,
  },
  {
    path: APP_ROUTES.LOGOUT_APP,
    component: LogOutAppComponent,
  },
  {
    path: APP_ROUTES.AUTH,
    canActivate: [NoAuthGuard, IsMultiTenantNot],
    loadChildren: () =>
      import("./modules/auth/auth.module").then((m) => m.AuthModule),
  },
  {
    path: APP_ROUTES.MULTITENANT_ACCESS,
    canActivate: [IsMultiTenant, NoAuthGuard],
    loadChildren: () =>
      import("./modules/multitenant-access/multitenant-access.module").then(
        (m) => m.MultitenantAccessModule,
      ),
  },
  {
    path: APP_ROUTES.DASHBOARD,
    data: {
      permissions: {
        only: dashboardPagePermissions,
        redirectTo: "/no-access",
      },
    },
    canActivate: [AuthGuard],
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/dashboard/dashboard.module").then(
        (m) => m.DashboardModule,
      ),
  },
  {
    path: APP_ROUTES.DIMENSIONS,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: customerDimensionsPermissions.concat(
          productsDimensionsPermissions,
        ),
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/dimensions/dimensions.module").then(
        (m) => m.DimensionsModule,
      ),
  },
  {
    path: APP_ROUTES.DIMENSION_ASSIGNMENT,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: customerDimensionsPermissions.concat(
          productsDimensionsPermissions,
        ),
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/dimension-assignment/dimension-assignment.module").then(
        (m) => m.DimensionAssignmentModule,
      ),
  },
  {
    path: APP_ROUTES.CUSTOMER_ORDERS,
    data: {
      permissions: {
        only: customerOrderPagePermissions,
        redirectTo: "/no-access",
      },
    },
    canActivate: [AuthGuard],
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/customer-orders-main/customer-orders-main.module").then(
        (m) => m.CustomerOrdersModule,
      ),
  },
  {
    path: APP_ROUTES.ADMINISTRATION,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: administrationPagePermissions,
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/administration/administration.module").then(
        (m) => m.AdministrationModule,
      ),
  },
  {
    path: APP_ROUTES.NO_ACCESS,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./modules/no-access/no-access.module").then(
        (m) => m.NoAccessModule,
      ),
  },
  {
    path: APP_ROUTES.PROFILE,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./modules/profile/profile.module").then((m) => m.ProfileModule),
  },
  {
    path: APP_ROUTES.REPORTS,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: reportsPageRoutes,
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/reports/reports.module").then((m) => m.ReportsModule),
  },
  {
    path: APP_ROUTES.CHART_VIEW,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: ["view_reports_charts_net_sales_by_dimension"],
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/charts-view/charts-view.module").then(
        (m) => m.ChartsViewModule,
      ),
  },
  {
    path: APP_ROUTES.HELP,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./modules/help/help.page.module").then((m) => m.HelpModule),
  },
  {
    path: APP_ROUTES.MANAGE_PAGE,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: managePagePermissions,
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/manage/manage.module").then((m) => m.ManageModule),
  },
  {
    path: APP_ROUTES.OPERATIONS_PAGE,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: operationsPagePermissions,
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/operations/operations.module").then(
        (m) => m.OperationsModule,
      ),
  },
  {
    path: APP_ROUTES.QUICK_SEARCH,
    canActivate: [AuthGuard],
    data: {
      permissions: {
        only: [
          "view_quick_search_search_customers",
          "view_quick_search_search_products",
        ],
        redirectTo: "/no-access",
      },
    },
    canActivateChild: [NgxPermissionsGuard],
    loadChildren: () =>
      import("./modules/quick-search/quick-search.module").then(
        (m) => m.QuickSearchModule,
      ),
  },
  {
    path: "dashboard",
    loadChildren: () =>
      import("./modules/rto-dashboard/rto-dashboard.module").then(
        (m) => m.RtoDashboardModule,
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: "reload",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
